/**
 * @module public/js/pages/gamificationDashboardUtils
 * @summary Display helpers for the gamification dashboard.
 */

/**
 * Format a decimal ratio as a whole percentage.
 *
 * @param {unknown} value - Decimal ratio.
 * @returns {string} Whole percentage label.
 */
export function formatGamificationPercent(value) {
  const numeric = Number(value);
  return `${Math.round((Number.isFinite(numeric) ? numeric : 0) * 100)}%`;
}

/**
 * Format a Date as an ISO date key in an IANA timezone.
 *
 * @param {Date} date - Date to format.
 * @param {string} timeZone - IANA timezone id.
 * @returns {string} YYYY-MM-DD date key.
 */
export function formatGamificationDateKey(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  );
  return `${values.year}-${values.month}-${values.day}`;
}

/**
 * Resolve a score status label.
 *
 * @param {unknown} score - Momentum score from 0 to 100.
 * @returns {{label: string, tone: string}} Display label and CSS tone.
 */
export function getMomentumScoreStatus(score) {
  const numeric = Number(score) || 0;
  if (numeric >= 85) return { label: 'On fire', tone: 'strong' };
  if (numeric >= 65) return { label: 'Building momentum', tone: 'steady' };
  return { label: 'Room to grow', tone: 'starting' };
}

/**
 * Build the three You-versus-Team effort-per-result benchmarks.
 *
 * @param {object} params - Comparison inputs.
 * @param {Record<string, any>} [params.team={}] - Team effort summary.
 * @param {Record<string, any>} [params.personal={}] - Seller effort summary.
 * @returns {Array<Record<string, any>>} Display-ready benchmark cards.
 */
export function buildEffortBenchmarkModels({ team = {}, personal = {} } = {}) {
  const definitions = [
    {
      id: 'answer',
      title: 'Effort per answer',
      target: 'answer',
      teamMetric: team?.answer || {},
      personalMetric: personal?.answer || {},
    },
    {
      id: 'go',
      title: 'Effort per GO',
      target: 'GO',
      teamMetric: team?.go || {},
      personalMetric: personal?.go || {},
    },
    {
      id: 'no-go',
      title: 'Effort per NO GO',
      target: 'NO GO',
      teamMetric: team?.noGo || {},
      personalMetric: personal?.noGo || {},
    },
  ];

  return definitions.map((definition) => {
    const teamAverage = Number(definition.teamMetric?.tasksPerResult);
    const personalAverage = Number(definition.personalMetric?.tasksPerResult);
    const teamSampleSize = Number(definition.teamMetric?.resultCount) || 0;
    const personalSampleSize =
      Number(definition.personalMetric?.resultCount) || 0;
    const teamRate = Number(definition.teamMetric?.resultRate) || 0;
    const personalRate = Number(definition.personalMetric?.resultRate) || 0;
    const validTeam =
      Number.isFinite(teamAverage) && teamAverage > 0 && teamSampleSize > 0;
    const validPersonal =
      Number.isFinite(personalAverage) &&
      personalAverage > 0 &&
      personalSampleSize > 0;
    const max = Math.max(
      validTeam ? teamAverage : 0,
      validPersonal ? personalAverage : 0,
      1
    );
    const smallestSample = Math.min(teamSampleSize, personalSampleSize);
    const confidence =
      smallestSample >= 10
        ? 'strong'
        : smallestSample >= 5
          ? 'directional'
          : 'thin';
    const confidenceText =
      confidence === 'strong'
        ? 'Strong sample'
        : confidence === 'directional'
          ? 'Directional sample'
          : 'Small sample';
    let summaryText = `More ${definition.target} results are needed for a comparison.`;

    if (validTeam && validPersonal) {
      const difference = Math.round((personalAverage - teamAverage) * 10) / 10;
      if (difference === 0) {
        summaryText = `You and the team use the same number of completed tasks per ${definition.target}.`;
      } else if (difference < 0) {
        summaryText = `You use ${Math.abs(difference).toFixed(1)} fewer completed tasks per ${definition.target} than the team.`;
      } else {
        summaryText = `You use ${difference.toFixed(1)} more completed tasks per ${definition.target} than the team.`;
      }
    }

    return {
      id: definition.id,
      title: definition.title,
      confidence,
      confidenceText,
      summaryText,
      personal: {
        label: 'You',
        average: validPersonal ? personalAverage : null,
        sampleSize: personalSampleSize,
        rate: personalRate,
        rateLabel: formatGamificationPercent(personalRate),
        width: validPersonal
          ? Math.max(8, Math.round((personalAverage / max) * 100))
          : 0,
      },
      team: {
        label: 'Team',
        average: validTeam ? teamAverage : null,
        sampleSize: teamSampleSize,
        rate: teamRate,
        rateLabel: formatGamificationPercent(teamRate),
        width: validTeam
          ? Math.max(8, Math.round((teamAverage / max) * 100))
          : 0,
      },
    };
  });
}
